// Components
import { ErrorBoundary } from 'react-error-boundary';
import {
  Button, PageLayout, Dialog, DialogError,
} from '@repo/zolintra-ui';
// Hooks
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../../hooks/useDialog';
import { useAppContext } from '../../store/AppContext';
import useResources from '../../hooks/domain/useResources';
import useAuth from '../../hooks/useAuth';
// Styles
import styles from './RemoteWorkingAndLeave.module.css';
// Templates
import NewRemoteWorkingForm from './formTemplates/NewRemoteWorkingForm';
// Permissions
import hasPermission, { PERMISSIONS } from '../../permissions/hasPermission';
// SubViews
import ListSubViewRemoteWorking from './subViews/ListSubViewRemoteWorking';

function ListRemoteWorking() {
  // Hooks
  const [{ user }] = useAppContext();
  const { t } = useTranslation();
  const { isOpen, dialog, setDialog } = useDialog();
  const { getResourceDisplayName } = useResources();
  const { handleLogout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Permissions
  const hasAtLeastOnePostCreationPermission = (
    hasPermission(PERMISSIONS.CREATE_ANY_NEW_REMOTE_WORKING_POST, user.prm)
    || hasPermission(PERMISSIONS.CREATE_OWN_NEW_REMOTE_WORKING_POST, user.prm)
    || hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm)
    || hasPermission(PERMISSIONS.CREATE_OWN_NEW_LEAVE_POST, user.prm)
  );
  const hasConfigurationPermission = (
    (
      hasPermission(PERMISSIONS.GET_CONFIGURATION, user.prm)
      && hasPermission(PERMISSIONS.UPDATE_CONFIGURATION, user.prm)
    ) || (
      hasPermission(PERMISSIONS.GET_ANY_RESOURCE, user.prm)
      && hasPermission(PERMISSIONS.IDENTITY_ACCESS_GET_ANY_RESOURCE, user.prm)
      && hasPermission(PERMISSIONS.UPDATE_ANY_RESOURCE, user.prm)
    )
  );
  const hasGetAnyPostPermission = (
    hasPermission(PERMISSIONS.GET_ANY_REMOTE_WORKING_POST, user.prm)
  );

  // Handlers
  const handleNewPostBtnClick = () => {
    setDialog({
      type: 'SHOW_DIALOG',
      payload: {
        title: t('remoteWorkingAndLeave.newPostDialog.title'),
        children: (
          <ErrorBoundary FallbackComponent={DialogError}>
            <NewRemoteWorkingForm
              event={{
                resourceId: { label: getResourceDisplayName(user), value: user.id },
              }}
            />
          </ErrorBoundary>
        ),
        onClose: () => {
          setDialog({ type: 'HIDE_DIALOG' });
        },
      },
    });
  };

  return (
    <PageLayout
      title={t('remoteWorkingAndLeave.remoteTitle')}
      user={{
        name: getResourceDisplayName(user, true),
        photo: user.photo,
      }}
      logout={{ label: t('common.logout'), handleLogout }}
    >
      <div className={styles.layout}>
        <div className={styles['sub-menu']}>
          <div className={styles['sub-menu-left']}>
            {hasGetAnyPostPermission ? (
              <>
                <Button
                  onClick={() => navigate('/remote-workings')}
                  color={pathname === '/remote-workings' ? 'primary200' : 'neutral500'}
                  shape="rectangle"
                  variant="bordered"
                >
                  {t('remoteWorkingAndLeave.userListBtnLabel')}
                </Button>
                <Button
                  onClick={() => navigate('/remote-workings/admin')}
                  color={pathname === '/remote-workings/admin' ? 'primary200' : 'neutral500'}
                  shape="rectangle"
                  variant="bordered"
                >
                  {t('remoteWorkingAndLeave.adminListBtnLabel')}
                </Button>
              </>
            ) : null}
          </div>

          <div className={styles['sub-menu-right']}>
            {hasConfigurationPermission ? (
              <a href="/settings">
                <Button
                  color="primary500"
                  shape="rounded"
                  icon="setting"
                  variant="bordered"
                  fontWeight="bold"
                  onClick={() => {}}
                >
                  {t('remoteWorkingAndLeave.settingsBtnLabel')}
                </Button>
              </a>
            ) : null}

            {hasAtLeastOnePostCreationPermission ? (
              <Button
                onClick={handleNewPostBtnClick}
                color="primary500"
                shape="rounded"
                variant="filled"
                fontWeight="bold"
              >
                {t('remoteWorkingAndLeave.newBtnLabel')}
              </Button>
            ) : null}
          </div>
        </div>

        <div className={styles['view-content']}>
          <ListSubViewRemoteWorking />
        </div>
      </div>

      <Dialog
        title={dialog.title}
        onClose={() => dialog.onClose()}
        isOpen={isOpen}
        topRightContent={dialog.topRightContent}
        footer={dialog.footer}
      >
        {dialog.children}
      </Dialog>
    </PageLayout>
  );
}

ListRemoteWorking.propTypes = {};

export default ListRemoteWorking;
